import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Building2, ExternalLink, Search, QrCode, X, Download } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import Logo from "@/components/Logo";

export default function Directory() {
  const { regToken } = useParams();
  const [dir, setDir] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [selected, setSelected] = useState(null);
  const posterRef = useRef();

  const downloadPoster = () => {
    const src = posterRef.current?.querySelector("canvas");
    if (!src || !selected) return;
    const W = 800, H = 1120, cv = document.createElement("canvas");
    cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#081426"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = dir.brand_color || "#D4AF37"; ctx.fillRect(0, 0, W, 18);
    ctx.textAlign = "center";
    ctx.fillStyle = dir.brand_color || "#D4AF37"; ctx.font = "bold 34px Georgia";
    ctx.fillText(dir.company_name || "", W / 2, 100);
    ctx.fillStyle = "#F6F1E4"; ctx.font = "bold 48px Georgia";
    ctx.fillText(selected.full_name || "", W / 2, 185);
    ctx.fillStyle = dir.brand_color || "#D4AF37"; ctx.font = "24px Arial";
    ctx.fillText(selected.job_title || "", W / 2, 228);
    let qy = 290;
    if (selected.tagline) {
      ctx.fillStyle = "#c9d3e0"; ctx.font = "italic 20px Georgia";
      ctx.fillText(`\u201C${selected.tagline}\u201D`, W / 2, 262);
      qy = 300;
    }
    const qs = 460, qx = (W - qs) / 2;
    ctx.fillStyle = "#ffffff"; ctx.fillRect(qx - 24, qy - 24, qs + 48, qs + 48);
    ctx.drawImage(src, qx, qy, qs, qs);
    ctx.fillStyle = "#F6F1E4"; ctx.font = "28px Arial";
    ctx.fillText("Scan to connect", W / 2, qy + qs + 96);
    ctx.fillStyle = "#8B9BB4"; ctx.font = "18px Arial";
    ctx.fillText(`${window.location.origin}/c/${selected.slug}`, W / 2, qy + qs + 134);
    ctx.fillStyle = dir.brand_color || "#D4AF37"; ctx.font = "bold 24px Georgia";
    ctx.fillText("iWurks360DigitAL", W / 2, H - 50);
    const a = document.createElement("a");
    a.download = `iwurks360-qr-poster-${selected.slug}.png`;
    a.href = cv.toDataURL("image/png"); a.click();
  };

  useEffect(() => { api.get(`/public/directory/${regToken}`).then((r) => setDir(r.data)).catch(() => setNotFound(true)); }, [regToken]);

  const departments = useMemo(() => {
    if (!dir) return [];
    return [...new Set(dir.cards.map((c) => c.department).filter(Boolean))].sort();
  }, [dir]);

  const filtered = useMemo(() => {
    if (!dir) return [];
    const term = q.trim().toLowerCase();
    return dir.cards.filter((c) => {
      const matchesQ = !term || (c.full_name || "").toLowerCase().includes(term) || (c.job_title || "").toLowerCase().includes(term);
      const matchesD = !dept || c.department === dept;
      return matchesQ && matchesD;
    });
  }, [dir, q, dept]);

  if (notFound) return <div className="min-h-screen flex flex-col items-center justify-center gap-4"><Logo /><p className="text-[#8B9BB4]">Directory not found.</p></div>;
  if (!dir) return <div className="min-h-screen flex items-center justify-center text-[#8B9BB4]">Loading…</div>;

  const gold = dir.brand_color || "#D4AF37";
  const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen iw-noise relative">
      <div className="relative z-10">
        <div className="h-40 relative flex items-end" style={{ background: `linear-gradient(120deg, ${gold}, #081426 90%)` }}>
          <div className="max-w-5xl mx-auto w-full px-6 pb-6 flex items-center gap-4">
            {dir.logo_url ? <img src={fileUrl(dir.logo_url)} alt="" className="h-16 w-16 object-contain rounded-xl bg-white/90 p-2" />
              : <div className="h-16 w-16 rounded-xl bg-white/90 flex items-center justify-center"><Building2 style={{ color: gold }} /></div>}
            <div>
              <h1 className="font-display text-3xl font-bold text-white">{dir.company_name}</h1>
              {dir.domain && <p className="text-white/80 text-sm">{dir.domain}</p>}
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-10">
          <p className="overline" style={{ color: gold }}>Team Directory</p>
          <h2 className="font-display text-2xl font-bold mt-1 mb-6">{dir.cards.length} team member{dir.cards.length !== 1 ? "s" : ""}</h2>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B9BB4]" />
              <input className="iw-input pl-9" placeholder="Search by name or title…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dir-search" />
            </div>
            {departments.length > 0 && (
              <select className="iw-input sm:w-56" value={dept} onChange={(e) => setDept(e.target.value)} data-testid="dir-dept-filter">
                <option value="">All departments</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
          </div>

          {filtered.length === 0 ? <p className="text-[#8B9BB4]">No matching team members.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((c) => (
                <button key={c.slug} onClick={() => setSelected(c)} className="iw-card p-5 flex items-center gap-4 text-left w-full" data-testid={`dir-card-${c.slug}`}>
                  <div className="h-14 w-14 rounded-xl overflow-hidden bg-[#142B5A] flex items-center justify-center shrink-0">
                    {c.avatar_url ? <img src={fileUrl(c.avatar_url)} alt="" className="h-full w-full object-cover" />
                      : <span className="font-display font-bold" style={{ color: gold }}>{initials(c.full_name)}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#F6F1E4] truncate">{c.full_name || "Unnamed"}</p>
                    <p className="text-sm text-[#8B9BB4] truncate">{c.job_title}</p>
                    {c.tagline && <p className="text-xs italic text-[#8B9BB4]/80 truncate" data-testid={`dir-tile-tagline-${c.slug}`}>“{c.tagline}”</p>}
                    {c.department && <p className="text-xs truncate" style={{ color: gold }}>{c.department}</p>}
                  </div>
                  <QrCode size={18} className="ml-auto shrink-0" style={{ color: gold }} />
                </button>
              ))}
            </div>
          )}
          {selected && (
            <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/75" onClick={() => setSelected(null)}>
              <div className="iw-card p-8 w-full max-w-xs text-center relative" onClick={(e) => e.stopPropagation()} data-testid="dir-qr-modal">
                <button onClick={() => setSelected(null)} className="absolute top-3 right-3 text-[#8B9BB4] hover:text-[#F6F1E4]" data-testid="dir-qr-close"><X size={18} /></button>
                <h3 className="font-display text-xl font-bold">{selected.full_name}</h3>
                <p className="text-sm" style={{ color: gold }}>{selected.job_title}</p>
                {selected.tagline && <p className="text-xs italic text-[#c9d3e0] mt-1 mb-4" data-testid="dir-tagline">“{selected.tagline}”</p>}
                {!selected.tagline && <div className="mb-5" />}
                <div className="bg-white p-4 rounded-xl inline-block"><QRCodeCanvas value={`${window.location.origin}/c/${selected.slug}`} size={190} level="H" fgColor="#081426" /></div>
                <div ref={posterRef} style={{ position: "absolute", left: "-9999px" }} aria-hidden="true"><QRCodeCanvas value={`${window.location.origin}/c/${selected.slug}`} size={460} level="H" fgColor="#081426" /></div>
                <p className="text-xs text-[#8B9BB4] mt-4">Scan to open the digital card</p>
                <button onClick={downloadPoster} className="btn-ghost w-full py-2.5 rounded-full mt-4 flex items-center justify-center gap-2" data-testid="dir-download-poster"><Download size={15} /> Download QR poster</button>
                <Link to={`/c/${selected.slug}`} className="btn-gold w-full py-2.5 rounded-full mt-2 flex items-center justify-center gap-2" style={{ background: gold }} data-testid="dir-open-card"><ExternalLink size={15} /> Open card</Link>
              </div>
            </div>
          )}
          <div className="flex justify-center mt-10 opacity-60"><Logo size={20} /></div>
        </div>
      </div>
    </div>
  );
}
